import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './dialog.jsx';
import { cn } from '../lib/utils.js';

/** @param {import('react').ComponentProps<typeof CommandPrimitive>} props */
export function Command({ className, ...props }) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn('flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground', className)}
      {...props}
    />
  );
}

/**
 * ⌘K palette container.
 * @param {{ open: boolean, onOpenChange: (open: boolean) => void, title?: string, description?: string, children: import('react').ReactNode, shouldFilter?: boolean }} props
 */
export function CommandDialog({ open, onOpenChange, title = 'Suche', description = 'Suchbegriff eingeben', children, shouldFilter = false }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[20%] translate-y-0 overflow-hidden p-0" showClose={false}>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>
        <Command shouldFilter={shouldFilter} label={title}>
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/** @param {import('react').ComponentProps<typeof CommandPrimitive.Input>} props */
export function CommandInput({ className, ...props }) {
  return (
    <div className="flex items-center gap-2 border-b px-3">
      <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn('flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:opacity-50', className)}
        {...props}
      />
    </div>
  );
}

/** @param {import('react').ComponentProps<typeof CommandPrimitive.List>} props */
export function CommandList({ className, ...props }) {
  return <CommandPrimitive.List data-slot="command-list" className={cn('max-h-80 overflow-y-auto overflow-x-hidden p-1', className)} {...props} />;
}

/** @param {import('react').ComponentProps<typeof CommandPrimitive.Empty>} props */
export function CommandEmpty({ className, ...props }) {
  return <CommandPrimitive.Empty data-slot="command-empty" className={cn('py-6 text-center text-sm text-muted-foreground', className)} {...props} />;
}

/** @param {import('react').ComponentProps<typeof CommandPrimitive.Group>} props */
export function CommandGroup({ className, ...props }) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

/** @param {import('react').ComponentProps<typeof CommandPrimitive.Item>} props */
export function CommandItem({ className, ...props }) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[disabled=true]:opacity-50 [&_svg]:size-4',
        className,
      )}
      {...props}
    />
  );
}
